import { useMutation } from 'react-apollo'
import { CellularPayPrompt, CellularPayResult } from './types'
import { isAmountChanged } from './amount-changed'
import {
  PW_PAY_CELLULAR_INVOICE_WITH_PAYMENT_METHOD,
  PW_PAY_CELLULAR_INVOICE_WITH_SAVED_CARD
} from '../../../apollo/api/paywall/mutations'

// What one call to a pay mutation came back with.
export type Charge =
  // `paymentMethodId` is the card it was paid with; null for the saved card.
  | { kind: 'result'; result: CellularPayResult; paymentMethodId: string | null }
  | { kind: 'noResult' }
  // The invoice no longer costs what the Pay button said; nothing was charged.
  | { kind: 'amountChanged' }
  // Saving the card failed and the customer has already been told why.
  | { kind: 'reported' }

const asCharge = (
  result: CellularPayResult | null | undefined,
  paymentMethodId: string | null
): Charge => (result ? { kind: 'result', result, paymentMethodId } : { kind: 'noResult' })

// Pays the prompt's invoice with a card: the saved one when paymentMethodId is null.
// It always sends the amount on the Pay button, so the paywall refuses rather than
// charge a different one. Calling it again for an invoice that is already paid
// never charges: the paywall finishes the post-payment steps and says it is paid.
export const usePayInvoice = (accountId: string, prompt: CellularPayPrompt) => {
  const [paySavedCard] = useMutation(PW_PAY_CELLULAR_INVOICE_WITH_SAVED_CARD)
  const [payWithPaymentMethod] = useMutation(PW_PAY_CELLULAR_INVOICE_WITH_PAYMENT_METHOD)

  const mutate = async (paymentMethodId: string | null): Promise<CellularPayResult | null> => {
    const variables = {
      accountId,
      invoiceId: prompt.invoiceId,
      expectedAmountUsd: prompt.amountDueUsd
    }
    if (!paymentMethodId) {
      const { data } = await paySavedCard({ variables })
      return data?.payCellularInvoiceWithSavedCard
    }
    const { data } = await payWithPaymentMethod({ variables: { ...variables, paymentMethodId } })
    return data?.payCellularInvoiceWithPaymentMethod
  }

  return async (paymentMethodId: string | null): Promise<Charge> => {
    try {
      return asCharge(await mutate(paymentMethodId), paymentMethodId)
    } catch (error) {
      if (isAmountChanged(error)) return { kind: 'amountChanged' }
      throw error
    }
  }
}
